/* eslint-disable @typescript-eslint/no-unused-vars */
import React, { useState, useEffect } from "react";
import { Button } from "../ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "../ui/card";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "../ui/table";
import { Avatar, AvatarImage, AvatarFallback } from "../ui/avatar";
import { Switch } from "../ui/switch";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../ui/dialog";
import { useToast } from "../ui/use-toast";
import { Plus, Edit, Trash2, Eye } from "lucide-react";
import CustomLoader from "../ui/custom-loader";
import { amenitiesActions } from "../../actions/amenities.action";
import { Amenity, AmenityFormData } from "../../types/amenities/amenities";
import AmenityForm from "./amenity-form";

const AmenitiesManagement = () => {
  const [amenities, setAmenities] = useState<Amenity[]>([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editingAmenity, setEditingAmenity] = useState<Amenity | null>(null);
  const [formLoading, setFormLoading] = useState(false);
  const [deleteLoading, setDeleteLoading] = useState<string | null>(null);
  const [toggleLoading, setToggleLoading] = useState<string | null>(null);
  const [deleteConfirmId, setDeleteConfirmId] = useState<string | null>(null);
  const [deleteName, setDeleteName] = useState<string | null>(null);
  const { toast } = useToast();

  useEffect(() => {
    fetchAmenities();
  }, []);

  const fetchAmenities = async () => {
    try {
      setLoading(true);
      const response = await amenitiesActions.getAmenities();
      setAmenities(response.data || []);
    } catch (error) {
      toast({
        title: "Error",
        description: "Failed to fetch amenities",
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  };

  const handleAddAmenity = () => {
    setEditingAmenity(null);
    setShowForm(true);
  };

  const handleEditAmenity = (amenity: Amenity) => {
    setEditingAmenity(amenity);
    setShowForm(true);
  };

  const handleDeleteAmenity = async (id: string) => {
    setDeleteConfirmId(id);
    const amenity = amenities.find((a) => a._id === id);
    setDeleteName(amenity?.name || null);
  };

  const confirmDeleteAmenity = async () => {
    if (!deleteConfirmId) return;
    try {
      setDeleteLoading(deleteConfirmId);
      await amenitiesActions.deleteAmenity(deleteConfirmId);
      toast({
        title: "Success",
        description: "Amenity deleted successfully",
      });
      setDeleteConfirmId(null);
      setDeleteName(null);
      fetchAmenities();
    } catch (error) {
      toast({
        title: "Error",
        description: "Failed to delete amenity",
        variant: "destructive",
      });
    } finally {
      setDeleteLoading(null);
    }
  };

  const cancelDeleteAmenity = () => {
    setDeleteConfirmId(null);
    setDeleteName(null);
  };

  const handleToggleStatus = async (id: string, isActive: boolean) => {
    try {
      setToggleLoading(id);
      await amenitiesActions.updateAmenity(id, { isActive: !isActive });
      toast({
        title: "Success",
        description: `Amenity ${!isActive ? "activated" : "deactivated"} successfully`,
      });
      fetchAmenities();
    } catch (error) {
      toast({
        title: "Error",
        description: "Failed to update amenity status",
        variant: "destructive",
      });
    } finally {
      setToggleLoading(null);
    }
  };

  const handleFormSubmit = async (data: AmenityFormData) => {
    try {
      setFormLoading(true);
      if (editingAmenity) {
        await amenitiesActions.updateAmenity(editingAmenity._id, data);
        toast({
          title: "Success",
          description: "Amenity updated successfully",
        });
      } else {
        await amenitiesActions.addAmenity(data);
        toast({
          title: "Success",
          description: "Amenity added successfully",
        });
      }
      setShowForm(false);
      fetchAmenities();
    } catch (error) {
      toast({
        title: "Error",
        description: "Failed to save amenity",
        variant: "destructive",
      });
    } finally {
      setFormLoading(false);
    }
  };

  const handleFormCancel = () => {
    setShowForm(false);
    setEditingAmenity(null);
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <CustomLoader size="lg" />
      </div>
    );
  }
  return (
    <div className="space-y-6  p-6 rounded-lg">
      <div className="flex justify-between items-center">
        <h1 className="text-3xl text-black font-bold">Amenities Management</h1>
        <Button
          className="bg-gradientButton hover:bg-primary/90 text-white dark:bg-primary/80 dark:hover:bg-primary/70 dark:text-black/90 transition-colors duration-200 font-medium shadow-sm hover:shadow-md"
          onClick={handleAddAmenity}
        >
          <Plus className="h-4 w-4 mr-2 " color="white" />
          Add Amenity
        </Button>
      </div>
      <div className="w-full mx-auto  rounded-lg">
        <Card>
          <CardHeader>
            <CardTitle>Amenities List</CardTitle>
          </CardHeader>
          <CardContent>
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Image</TableHead>
                  <TableHead>Name</TableHead>
                  <TableHead>Slug</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead>Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {amenities.map((amenity) => {
                  const isInactive = amenity.isActive === false;
                  return (
                    <TableRow
                      key={amenity._id}
                      className={isInactive ? "bg-red-200" : ""}
                    >
                      <TableCell>
                        <Avatar className="h-10 w-10">
                          <AvatarImage src={amenity.image} alt={amenity.name} />
                          <AvatarFallback>
                            {amenity.name.charAt(0).toUpperCase()}
                          </AvatarFallback>
                        </Avatar>
                      </TableCell>
                      <TableCell className="font-medium">
                        {amenity.name}
                      </TableCell>
                      <TableCell>{amenity.slugText}</TableCell>
                      <TableCell>
                        <span
                          className={`ml-2 px-2 py-0.5 text-xs rounded ${isInactive ? "bg-gray-300 text-gray-700" : "bg-green-200 text-green-800"}`}
                        >
                          {isInactive ? "Inactive" : "Active"}
                        </span>
                      </TableCell>
                      <TableCell>
                        <div className="flex space-x-2">
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => handleEditAmenity(amenity)}
                          >
                            <Edit className="h-4 w-4" />
                          </Button>
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => handleDeleteAmenity(amenity._id)}
                            disabled={deleteLoading === amenity._id}
                          >
                            {deleteLoading === amenity._id ? (
                              <CustomLoader size="sm" />
                            ) : (
                              <Trash2 className="h-4 w-4" />
                            )}
                          </Button>
                        </div>
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </CardContent>
        </Card>
      </div>
      <Dialog open={showForm} onOpenChange={setShowForm}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>
              {editingAmenity ? "Edit Amenity" : "Add New Amenity"}
            </DialogTitle>
          </DialogHeader>
          <AmenityForm
            amenity={editingAmenity || undefined}
            onSubmit={handleFormSubmit}
            onCancel={handleFormCancel}
            loading={formLoading}
          />
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <Dialog open={!!deleteConfirmId} onOpenChange={cancelDeleteAmenity}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Confirm Delete</DialogTitle>
          </DialogHeader>
          <div className="py-4">
            Are you sure you want to delete the amenity{" "}
            <span className="font-semibold">{deleteName}</span>?
          </div>
          <div className="flex justify-end gap-2">
            <Button
              variant="outline"
              onClick={cancelDeleteAmenity}
              disabled={deleteLoading === deleteConfirmId}
            >
              No
            </Button>
            <Button
              variant="destructive"
              onClick={confirmDeleteAmenity}
              disabled={deleteLoading === deleteConfirmId}
            >
              {deleteLoading === deleteConfirmId ? (
                <CustomLoader size="sm" />
              ) : (
                "Yes"
              )}
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default AmenitiesManagement;
